import React, { useState, useRef, useMemo } from "react";
import { Upload, FileText, X, Eye, AlertCircle, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";

/**
 * Generic bulk upload modal — accepts CSV (drag/drop or file pick) OR JSON array.
 * Auto-parses CSV → array of objects using header row, casts booleans + numbers.
 * Shows preview table, then POSTs to `endpoint` with `{ [payloadKey]: rows }`.
 *
 * Props:
 *   open, onClose
 *   title
 *   endpoint        e.g. "/admin/ads/bulk"
 *   payloadKey      e.g. "ads" | "backgrounds" | "tracks"
 *   csvHeaders      [{ key, label, hint?, required? }]
 *   sampleJson      string of example JSON
 *   sampleCsv       string of example CSV
 *   onSuccess(data) called with the API response { created, errors, count }
 */
export default function BulkUploadModal({
  open, onClose, title, endpoint, payloadKey,
  csvHeaders, sampleJson, sampleCsv, onSuccess,
}) {
  const [mode, setMode] = useState("csv"); // 'csv' | 'json'
  const [text, setText] = useState(sampleCsv);
  const [parsed, setParsed] = useState(null);
  const [parseError, setParseError] = useState("");
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const fileInputRef = useRef(null);

  React.useEffect(() => {
    if (!open) return;
    setMode("csv"); setText(sampleCsv); setParsed(null); setParseError("");
  }, [open, sampleCsv]);

  // Live-parse whenever text changes
  React.useEffect(() => {
    if (!text.trim()) { setParsed(null); setParseError(""); return; }
    try {
      const rows = mode === "csv" ? parseCsv(text) : JSON.parse(text);
      if (!Array.isArray(rows)) throw new Error("Expected an array of rows");
      setParsed(rows);
      setParseError("");
    } catch (e) { setParsed(null); setParseError(e.message || "Parse error"); }
  }, [text, mode]);

  const previewKeys = csvHeaders.map(h => h.key);
  const previewRows = useMemo(() => parsed?.slice(0, 5) || [], [parsed]);

  if (!open) return null;

  const handleFile = async (file) => {
    if (!file) return;
    const ext = file.name.toLowerCase().split(".").pop();
    if (ext === "json") setMode("json");
    else setMode("csv");
    const txt = await file.text();
    setText(txt);
  };

  const onDrop = async (e) => {
    e.preventDefault(); setDrag(false);
    const file = e.dataTransfer.files?.[0];
    if (file) await handleFile(file);
  };

  const submit = async () => {
    if (!parsed?.length) { setParseError("Nothing to submit"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, { [payloadKey]: parsed });
      onSuccess(data);
    } catch (e) {
      setParseError(formatApiError(e?.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="bulk-upload-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="card w-full max-w-3xl max-h-[92vh] overflow-y-auto p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="font-display font-black text-xl uppercase">{title}</div>
            <div className="text-xs text-[#A1A1AA]">Drop a CSV/JSON file or paste below.</div>
          </div>
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-[#FAFAFA]"><X size={18}/></button>
        </div>

        {/* Mode toggle + sample */}
        <div className="flex flex-wrap gap-2 mb-3">
          <button onClick={() => { setMode("csv"); setText(sampleCsv); }}
            data-testid="bulk-mode-csv"
            className={`chip text-xs ${mode === "csv" ? "active" : ""}`}>CSV</button>
          <button onClick={() => { setMode("json"); setText(sampleJson); }}
            data-testid="bulk-mode-json"
            className={`chip text-xs ${mode === "json" ? "active" : ""}`}>JSON</button>
          <div className="flex-1"/>
          <button onClick={() => fileInputRef.current?.click()}
            data-testid="bulk-file-pick"
            className="btn-secondary text-xs px-3 py-1.5">
            <Upload size={12} className="inline mr-1"/>Pick file
          </button>
          <input ref={fileInputRef} type="file" accept=".csv,.json,.txt"
            className="hidden" onChange={(e) => handleFile(e.target.files?.[0])}/>
        </div>

        {/* Drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          data-testid="bulk-dropzone"
          className={`border-2 border-dashed rounded-xl p-3 mb-3 transition ${
            drag ? "border-[#D4FF00] bg-[#D4FF00]/10" : "border-[#3D2845]"
          }`}>
          <textarea value={text} onChange={(e) => setText(e.target.value)}
            rows={10}
            data-testid="bulk-textarea"
            placeholder={mode === "csv" ? "Paste CSV with header row" : "Paste JSON array"}
            className="input-dark w-full px-3 py-2 text-xs font-mono resize-y bg-transparent border-0 focus:ring-0"/>
          <div className="text-[10px] text-[#52525B] mt-1 flex items-center gap-1">
            <FileText size={10}/> {drag ? "Drop here…" : "Drag a .csv or .json file anywhere on this box"}
          </div>
        </div>

        {/* Parse status */}
        {parseError && (
          <div className="mb-3 px-3 py-2 rounded-lg bg-[#FF007F]/10 border border-[#FF007F]/40 text-[#FF007F] text-xs flex items-start gap-2">
            <AlertCircle size={14} className="shrink-0 mt-0.5"/>
            <div>
              <div className="font-bold">Parse error</div>
              <div>{parseError}</div>
            </div>
          </div>
        )}
        {parsed && !parseError && (
          <div className="mb-3 px-3 py-2 rounded-lg bg-[#D4FF00]/10 border border-[#D4FF00]/40 text-[#D4FF00] text-xs flex items-center gap-2">
            <CheckCircle2 size={14} className="shrink-0"/>
            <div className="font-bold">{parsed.length} row{parsed.length === 1 ? "" : "s"} parsed</div>
          </div>
        )}

        {/* Preview */}
        {previewRows.length > 0 && (
          <div className="card overflow-x-auto mb-3" data-testid="bulk-preview">
            <div className="px-3 py-2 border-b border-[#3D2845] text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA] flex items-center gap-2">
              <Eye size={11}/>Preview · first {previewRows.length} of {parsed.length}
            </div>
            <table className="w-full text-xs">
              <thead className="bg-[#0F0F11]">
                <tr>{csvHeaders.map(h => (
                  <th key={h.key} className="p-2 text-left font-bold uppercase tracking-wider text-[10px] text-[#A1A1AA]">
                    {h.label}{h.required && <span className="text-[#FF007F]"> *</span>}
                  </th>
                ))}</tr>
              </thead>
              <tbody>
                {previewRows.map((row, i) => (
                  <tr key={`preview-${i}-${row.name || row.title || row.id || ""}`} className="border-t border-[#3D2845]">
                    {previewKeys.map(k => (
                      <td key={k} className="p-2 truncate max-w-[200px]">
                        {String(row[k] ?? "")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Header reference */}
        <details className="mb-4 text-xs text-[#A1A1AA]">
          <summary className="cursor-pointer hover:text-[#FAFAFA]">Field reference</summary>
          <ul className="mt-2 space-y-1 pl-4">
            {csvHeaders.map(h => (
              <li key={h.key}>
                <code className="text-[#D4FF00]">{h.key}</code>
                {h.required && <span className="text-[#FF007F]"> (required)</span>}
                {h.hint && <span className="text-[#52525B]"> — {h.hint}</span>}
              </li>
            ))}
          </ul>
        </details>

        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary text-xs">Cancel</button>
          <button onClick={submit} disabled={busy || !parsed?.length || !!parseError}
            data-testid="bulk-submit"
            className="btn-primary text-xs disabled:opacity-50 disabled:cursor-not-allowed">
            {busy ? "Uploading…" : `Upload ${parsed?.length || 0} row${parsed?.length === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── tiny CSV parser — handles quoted fields, commas, escaped quotes ───
function parseCsv(text) {
  const lines = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (c === '"') {
      if (inQuotes && text[i + 1] === '"') { cur += '"'; i++; }
      else inQuotes = !inQuotes;
    } else if (c === "\n" && !inQuotes) {
      lines.push(cur); cur = "";
    } else if (c === "\r" && !inQuotes) { /* skip */ }
    else { cur += c; }
  }
  if (cur.length) lines.push(cur);
  if (!lines.length) return [];

  const splitRow = (line) => {
    const out = []; let f = ""; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        if (inQ && line[i + 1] === '"') { f += '"'; i++; }
        else inQ = !inQ;
      } else if (c === "," && !inQ) { out.push(f); f = ""; }
      else f += c;
    }
    out.push(f);
    return out;
  };

  const headers = splitRow(lines[0]).map(h => h.trim());
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) continue;
    const cells = splitRow(line);
    const row = {};
    headers.forEach((h, idx) => {
      let v = (cells[idx] ?? "").trim();
      if (v === "") return;            // skip empty (so backend defaults apply)
      if (/^(true|false)$/i.test(v)) v = v.toLowerCase() === "true";
      else if (/^-?\d+(\.\d+)?$/.test(v)) v = Number(v);
      row[h] = v;
    });
    rows.push(row);
  }
  return rows;
}
